// app/routes/api/get-customer-invoices.ts
import { query } from "@solidjs/router";

import { getUserByEmail } from "~/_server";
import { getXeroClient } from "~/_server/xero";
import { Invoice } from "xero-node";
import { Invoice as InvoiceType, Contact as ContactType } from "xero-node";
import { StatusType } from "@aws-sdk/client-cognito-identity-provider";

type InvoiceDTO = {
	invoiceID?: string;
	invoiceNumber?: string;
	date?: string | null;
	dueDate?: string | null;
	status?: Invoice["status"];
	total?: number | null;
	amountDue?: number | null;
	amountPaid?: number | null;
	contact?: { contactID?: string; name?: string; emailAddress?: string };
};

const toDTO = (i: InvoiceType): InvoiceDTO => ({
	invoiceID: i.invoiceID,
	invoiceNumber: i.invoiceNumber,
	date: i.date ? new Date(i.date).toISOString() : null,
	dueDate: i.dueDate ? new Date(i.dueDate).toISOString() : null,
	status: i.status,
	total: i.total ?? null,
	amountDue: i.amountDue ?? null,
	amountPaid: i.amountPaid ?? null,
	contact: i.contact
		? {
				contactID: i.contact.contactID,
				name: i.contact.name,
				emailAddress: i.contact.emailAddress,
			}
		: undefined,
});

const toMs = (d: unknown): number =>
	d ? new Date(d as any).getTime() : -Infinity;

const reverseDate = (d: Date): string =>
	`${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;

export const createInvoice = async (
	userEmail: string,
	userName: string,
	lineItems: InvoiceType["lineItems"],
	invoiceId: number,
	date: string = "",
): Promise<string> => {
	"use server";

	const xeroClient = await getXeroClient();

	if (xeroClient) {
		const tenantId = xeroClient.tenants?.[0]?.tenantId;

		const contact = await getContactByEmail(userEmail, true);

		let dateObj;
		if (date == "") {
			dateObj = new Date();
		} else {
			dateObj = new Date(date);
		}

		const invoice = {
			invoiceNumber: `RR-${reverseDate(dateObj)}-${invoiceId}`,
			type: Invoice.TypeEnum.ACCREC,
			contact: { contactID: contact?.contactID ?? null, name: userName },
			lineItems,
			date: dateObj.toISOString(),
			dueDate: dateObj.toISOString(),
			status: Invoice.StatusEnum.AUTHORISED,
		};

		const result = await xeroClient.accountingApi.createInvoices(tenantId, {
			invoices: [invoice],
		});

		return result.body?.invoices?.[0]?.invoiceID ?? "";
	}
};

export const setInvoicePending = async (invoiceId: string) => {
	"use server";

	const xeroClient = await getXeroClient();

	if (xeroClient) {
		const tenantId = xeroClient.tenants?.[0]?.tenantId;

		return await xeroClient.accountingApi.updateInvoice(tenantId, invoiceId, {
			invoices: [{ reference: "StripePending" }],
		});
	}
};

const createContact = async (userEmail: string) => {
	if (xeroClient) {
		const xeroClient = await getXeroClient();

		const user = await getUserByEmail(userEmail);

		if (user) {
			const contact = {
				Name: "Acme Corp",
				EmailAddress: "billing@acme.com",
				FirstName: "John",
				LastName: "Doe",
				Addresses: [
					{
						AddressType: "POBOX",
						AddressLine1: "123 Test Street",
						City: "London",
						PostalCode: "SW1A 1AA",
						Country: "UK",
					},
				],
			} as ContactType;

			const result = await xeroClient.accountingApi.createContacts(
				xeroClient.tenants?.[0]?.tenantId,
				{
					contacts: [contact],
				},
			);

			return result.body.contacts?.[0];
		}
	}

	return null;
};

export const getContactByEmail = async (
	email: string,
	createIfNotExists = false,
) => {
	"use server";

	const xeroClient = await getXeroClient();

	if (xeroClient) {
		const tenantId = xeroClient.tenants?.[0]?.tenantId;

		const contactRes = await xeroClient.accountingApi.getContacts(
			tenantId,
			undefined,
			`EmailAddress=="${email}"`,
		);

		console.log("contact", contactRes?.body?.contacts);
		let contact = contactRes?.body?.contacts?.[0];
		if (!contact?.contactID && createIfNotExists) {
			contact = await createContact(email);

			//throw new Error(`No contact found for ${email}`);
		}

		if (contact?.contactID) return contact;
	}
};

export const getCustomerInvoices = query(async (email: string) => {
	"use server";

	const xeroClient = await getXeroClient();

	if (xeroClient) {
		try {
			const tenantId = xeroClient.tenants?.[0]?.tenantId;

			const contact = await getContactByEmail(email, false);

			if (contact) {
				// Step 2: Get invoices for this contact
				const invoiceRes = await xeroClient.accountingApi.getInvoices(
					tenantId,
					undefined,
					`Contact.ContactID==Guid("${contact.contactID}")`,
					undefined, // order
					undefined, // IDs
					undefined, // invoiceNumbers
					undefined, // contact IDs
					["AUTHORISED", "PAID"], // statuses
					undefined, // page
					undefined, // includeArchived
					false, // whereOnlyInvoices
				);

				const invoices = (invoiceRes.body.invoices ?? [])
					.slice()
					.filter(inv => inv.status != Invoice.StatusEnum.DELETED)
					.sort((a, b) => toMs(b.date) - toMs(a.date)) // date DESC, nulls last
					.map(toDTO);

				return invoices;
			} else {
				return [];
			}
		} catch (e: unknown) {
			console.error(e);
			return [{ error: (e as Error).message }];
		}
	}
}, "getCustomerInvoices");

